import { useLocation, Link, useNavigate } from "react-router-dom"
import { IconChevronRight } from "@tabler/icons-react"
import { type ComponentType, useState, useMemo, useEffect, useRef } from "react"

import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

type IconComponent = ComponentType<{ className?: string }>

type NavItem = {
  title: string
  url: string
  icon?: IconComponent
  comingSoon?: boolean
  requiresUpgrade?: boolean
  isOpen?: boolean
  items?: {
    title: string
    url: string
    comingSoon?: boolean
    icon?: IconComponent
  }[]
}

export function NavMain({
  items,
  isOnFreePlan = false,
}: {
  items: NavItem[]
  isOnFreePlan?: boolean
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const isInitialMount = useRef(true)

  // Get all group titles that have subitems
  const groupTitles = useMemo(() =>
    items.filter(item => item.items).map(item => item.title),
    [items]
  )

  // Initialize with the correct group expanded based on current URL
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    // Find which group the current path belongs to
    const activeGroup = items.find(item =>
      item.items?.some(sub => location.pathname === sub.url)
    )
    // If we're on a subitem route, only expand that group
    // Otherwise, expand all groups
    return activeGroup ? new Set([activeGroup.title]) : new Set(groupTitles)
  })

  // Handle group toggle - collapse all others and toggle the clicked one
  const handleGroupToggle = (title: string, shouldOpen: boolean) => {
    if (shouldOpen) {
      // When opening a group, close all others and open only this one
      setOpenGroups(new Set([title]))
    } else {
      // When closing, just remove this group
      setOpenGroups(prev => {
        const next = new Set(prev)
        next.delete(title)
        return next
      })
    }
  }

  // When clicking a subitem, ensure only that group is open
  const handleSubItemClick = (parentTitle: string) => {
    setOpenGroups(new Set([parentTitle]))
  }

  // React to route changes (after initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Find which group the current path belongs to
    const activeGroup = items.find(item =>
      item.items?.some(sub => location.pathname === sub.url)
    )

    if (activeGroup) {
      setOpenGroups(new Set([activeGroup.title]))
    }
  }, [location.pathname, items])

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {items.map((item) =>
            item.items ? (
              <Collapsible
                key={item.title}
                asChild
                open={openGroups.has(item.title)}
                onOpenChange={(open) => handleGroupToggle(item.title, open)}
                className="group/collapsible cursor-pointer"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild className="cursor-pointer">
                    <SidebarMenuButton
                      tooltip={item.title}
                      isActive={item.items.some(sub => location.pathname === sub.url)}
                      onClick={() => {
                        // Navigate to first subtab when clicking parent
                        if (item.items && item.items.length > 0) {
                          navigate(item.items[0].url)
                        }
                      }}
                    >
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <IconChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={location.pathname === subItem.url}
                          >
                            <Link
                              to={subItem.url}
                              onClick={() => handleSubItemClick(item.title)}
                            >
                              <span> {subItem.title}</span>
                              {subItem.comingSoon && (
                                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                                  Soon
                                </Badge>
                              )}
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            ) : (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={item.title}
                  asChild
                  isActive={location.pathname === item.url || location.pathname.startsWith(item.url + "/")}
                >
                  <Link to={item.url}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    {item.comingSoon && (
                      <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                        Soon
                      </Badge>
                    )}
                    {item.requiresUpgrade && isOnFreePlan && (
                      <Badge className="ml-auto text-[10px] px-1.5 py-0 bg-[#654EDA]/10 text-[#654EDA] hover:bg-[#654EDA]/20 border-0">
                        Upgrade
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
